// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery, useMutation } from '@apollo/client';
import { clientLogger } from '@seedplanner/logger';
import { PLANTING_STATE, utils } from '@seedplanner/shared-utils';
import Decimal from 'decimal.js';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '../../lib/base';
import { Help } from '../../lib/help';
import type {
  AddTraysToPlantingGroupDataQuery,
  AddTraysToPlantingGroupDataQueryVariables,
  AddTraysToPlantingGroupMutation,
  AddTraysToPlantingGroupMutationVariables,
} from '../../types/__generated__/graphql';
import { validateNumInput } from '../../utils';
import { ExitDialogButton } from '../common/button';
import { ErrorView } from '../common/error';
import { PageLoading } from '../common/loading';

const EDIT_PLANTING_NOTES_MUTATION = gql`
  mutation AddTraysToPlantingGroup($planting_group_id: Int!, $add_tray_count: Int!) {
    addTraysToPlantingGroup(input: { planting_group_id: $planting_group_id, add_tray_count: $add_tray_count }) {
      planting_created {
        id
      }
      tasks_created {
        id
      }
    }
  }
`;

const EDIT_PLANTING_TRAYS_DATA = gql`
  query AddTraysToPlantingGroupData($ids: String!) {
    plantings(
      filters: [{ name: "planting.id", operator: "in", value: $ids }]
    ) {
      edges {
        node {
          id
          trays_rounded
          planting_state
        }
      }
    }
  }
`;

interface Props {
  readonly plantingIds: readonly number[];
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export default function AddTraysToPlantingGroupDialog({ isOpen, onClose, onSuccess, plantingIds }: Props) {
  const [getPlantingGroupTrays, { loading: plantingDataLoading, data: plantingsData }] = useLazyQuery<
    AddTraysToPlantingGroupDataQuery,
    AddTraysToPlantingGroupDataQueryVariables
  >(EDIT_PLANTING_TRAYS_DATA, {
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });
  const [addTraysToPlantingGroup, { loading: editPlantingGroupTraysLoading, error: editPlantingTraysError }] =
    useMutation<AddTraysToPlantingGroupMutation, AddTraysToPlantingGroupMutationVariables>(
      EDIT_PLANTING_NOTES_MUTATION,
    );

  React.useEffect(() => {
    if (isOpen) {
      void getPlantingGroupTrays({
        variables: {
          ids: plantingIds.map((t) => t).join(','),
        },
        onError: (errorIn) => {
          enqueueSnackbar(`Error getting planting data: ${errorIn.message}`, {
            variant: 'error',
            autoHideDuration: 10000,
          });
        },
      });
    }
  }, [isOpen, getPlantingGroupTrays, plantingIds]);

  const [addTrays, setAddTrays] = React.useState('');
  const [traysValidation, setTraysValidation] = React.useState('');
  const onChangeTrays = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setAddTrays(e.target.value);
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setTraysValidation(msg),
      validateGT: 0,
      validateInt: true,
      isRequired: true,
    });
  };
  const onAddTraysBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setTraysValidation(msg),
      validateGT: 0,
      validateInt: true,
      isRequired: true,
    });
  };

  const onSubmit = (_e?: React.MouseEvent | React.TouchEvent) => {
    void addTraysToPlantingGroup({
      variables: {
        planting_group_id: plantingIds[0],
        add_tray_count: Number.parseInt(addTrays),
      },
      update: (cache) => {
        cache.modify({
          fields: {
            farm_report(_, { DELETE }) {
              return DELETE;
            },
            harvests(_existingGroup, { DELETE }) {
              return DELETE;
            },
            plantings(_existingGroup, { DELETE }) {
              return DELETE;
            },
            taskGroup(_existingGroup, { DELETE }) {
              return DELETE;
            },
            task_groups(_existingGroup, { DELETE }) {
              return DELETE;
            },
            tasks(_existingTasks, { DELETE }) {
              return DELETE;
            },
            plantingGroup(_existingGroup, { DELETE }) {
              return DELETE;
            },
            planting_groups(_existingGroup, { DELETE }) {
              return DELETE;
            },
            plantingTimeGroup(_existingGroup, { DELETE }) {
              return DELETE;
            },
          },
        });
      },
      onCompleted: (data) => {
        if (data !== undefined && data.addTraysToPlantingGroup != null) {
          enqueueSnackbar('Update planting trays', { variant: 'success' });
          setAddTrays('');
          setTraysValidation('');
          onSuccess();
        } else {
          enqueueSnackbar('Something went wrong!', {
            variant: 'error',
            autoHideDuration: 10000,
          });
        }
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        clientLogger.error({ title: 'planting_group_trays_error', message: errorIn.message });
      },
    });
  };

  const disableFields = plantingDataLoading || editPlantingGroupTraysLoading;
  const isSubmitDisabled = plantingDataLoading || editPlantingGroupTraysLoading || traysValidation !== '';

  const currentPlantings = plantingsData?.plantings.edges.map(({ node }) => node) ?? [];
  const totalTrays = currentPlantings.reduce((acc, p) => utils.addTraysRaw(acc, Number.parseFloat(p.trays_rounded)), 0);
  const traysForOrders = currentPlantings
    .filter((p) => p.planting_state === PLANTING_STATE.ALLOCATED)
    .reduce((acc, p) => utils.addTraysRaw(acc, Number.parseFloat(p.trays_rounded)), 0);
  const traysForSurpluses = currentPlantings
    .filter((p) => p.planting_state === PLANTING_STATE.SURPLUS)
    .reduce((acc, p) => utils.addTraysRaw(acc, Number.parseFloat(p.trays_rounded)), 0);

  const onEnter = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      onSubmit();
    }
  };

  const traysToAdd = Number.parseFloat(addTrays) || 0;

  const form = (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={1}>
      <Box display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" gap={2}>
        <TextField
          aria-describedby="add-trays-count"
          id="add-trays-count"
          label="Trays to Add"
          value={addTrays}
          onChange={onChangeTrays}
          onBlur={onAddTraysBlur}
          onEnter={onEnter}
          disabled={disableFields}
          type="number"
          inputMode="numeric"
          numberMin={0}
          hasSubtext
          error={traysValidation !== ''}
          subtext={traysValidation}
        />
        <Help tooltip="Enter the number of trays to add to this group of trays. The trays added will not be assigned to any orders and will be surplus trays. Use this feature to override the exiting calculated tray count to better reflect the actual state of your farm." />
      </Box>
      <Typography variant="body1">{`Current trays: ${totalTrays}. Assigned trays: ${traysForOrders}. Surplus trays: ${traysForSurpluses}`}</Typography>
      <Typography variant="body1">
        {`New tray count: ${Decimal.add(totalTrays, traysToAdd).toNumber()}. New assigned trays: ${traysForOrders}. New surplus trays: ${Decimal.add(traysForSurpluses, traysToAdd).toNumber()}`}
      </Typography>
    </Box>
  );

  const dialogContent = editPlantingGroupTraysLoading ? (
    <PageLoading />
  ) : editPlantingTraysError ? (
    <ErrorView error={editPlantingTraysError} />
  ) : (
    form
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Add Trays To Planting</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>{dialogContent}</DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button sx={{ width: { xs: '100%', md: '50%' } }} variant="outlined" onClick={onClose} disabled={disableFields}>
          Cancel
        </Button>
        <Button
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
